"use client";

import React from "react";
import { Compass, BedDouble, PlaneTakeoff, ReceiptText, Check, ArrowUpRight, Sparkles, Layers } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ModuleData {
  id: string;
  number: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  deliverable: string;
  focusArea: string;
  outputFormat: string;
  keyFields: string;
  summary: string;
}
const modulesList: ModuleData[] = [{
  id: "vibe-survey",
  number: "01",
  title: "The Family Vibe & Alignment Survey",
  subtitle: "Pace, dietary, mobility constraints",
  icon: Compass,
  deliverable: "Custom intake database",
  focusArea: "Generational pace & energy",
  outputFormat: "Interactive survey & synthesis",
  keyFields: "Pace tier, dietary needs, rest preferences",
  summary: "Collects individual energy limits, morning vs. night habits, and mobility notes across all ages before planning begins."
}, {
  id: "stateroom-allocator",
  number: "02",
  title: "Accommodations & Stateroom Allocator",
  subtitle: "Suite mapping & quiet zones",
  icon: BedDouble,
  deliverable: "Architectural floorplan matrix",
  focusArea: "Proximity & quiet buffers",
  outputFormat: "Interactive villa / yacht grid",
  keyFields: "Floor level, ensuite access, noise tolerance",
  summary: "Maps grand villa suites, adjoining rooms for toddlers, and quiet buffer corridors for grandparents to prevent early-morning friction."
}, {
  id: "flight-matrix",
  number: "03",
  title: "The Real-Time Flight & Shuttle Matrix",
  subtitle: "Coordinating multi-city arrivals",
  icon: PlaneTakeoff,
  deliverable: "Consolidated arrivals terminal",
  focusArea: "Multi-origin logistics",
  outputFormat: "Live timeline & buffer schedule",
  keyFields: "Flight codes, baggage status, group transfers",
  summary: "Syncs staggered flight arrivals from multiple cities into single private transfer windows, with built-in delay buffers."
}, {
  id: "expense-splitter",
  number: "04",
  title: "The Multi-Payer Expense Splitter",
  subtitle: "Conflict-free billing",
  icon: ReceiptText,
  deliverable: "Multi-currency ledger",
  focusArea: "Shared vs. private allocation",
  outputFormat: "Automated balance calculator",
  keyFields: "Deposit tracker, split ratios, instant totals",
  summary: "Separates patriarch-funded anchor experiences from branch-specific excursions, calculating clean net balances automatically."
}];
export default function Modules() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="feature-modules" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 border-b border-border">
      {/* Background Decorative Orbit Rings */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        <div className="absolute -right-32 top-12 size-96 rounded-full border border-border" />
        <div className="absolute -right-16 top-28 size-64 rounded-full border border-border" />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="size-2 rounded-full bg-primary" />
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground font-sans">
                Core Planning Architecture
              </p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-foreground tracking-tight">
              Four core frameworks built into Notion
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
              Open the template to balance pace, assign suites, coordinate travel, and split expenses without friction.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 rounded-sm border border-border bg-card text-xs font-mono text-muted-foreground">
              v2.4 Ready
            </div>
            <div className="px-3 py-1.5 rounded-sm border border-border bg-card text-xs font-mono text-muted-foreground">
              4 Modules
            </div>
          </div>
        </div>

        {/* 4-Column Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-12">
          {modulesList.map((mod, i) => {
          const IconComponent = mod.icon;
          return <motion.div key={mod.id} data-index={i} variants={cardVariants} className="h-full flex">
                <Card className="relative flex flex-col justify-between w-full rounded-sm border border-border bg-card text-card-foreground shadow-sm transition-[border-color,box-shadow,transform] duration-300 hover:border-primary/40 hover:-translate-y-1 hover:shadow-md p-6">
                  <div>
                    {/* Header: Monogram Number & Icon */}
                    <div className="flex items-center justify-between pb-5 border-b border-border">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex items-center justify-center size-7 rounded-sm bg-primary text-primary-foreground text-xs font-mono font-semibold">
                          {mod.number}
                        </span>
                        <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground font-sans">
                          Framework
                        </span>
                      </div>
                      <div className="p-2 rounded-sm bg-muted/60 border border-border">
                        <IconComponent className="size-4 text-foreground stroke-[1.25]" />
                      </div>
                    </div>

                    {/* Card Titles */}
                    <div className="pt-5 pb-4">
                      <CardTitle className="text-lg lg:text-xl font-serif font-semibold text-foreground leading-snug">
                        {mod.title}
                      </CardTitle>
                      <CardDescription className="mt-1.5 text-xs font-sans font-medium text-primary">
                        {mod.subtitle}
                      </CardDescription>
                    </div>

                    {/* Summary text */}
                    <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed pb-5">
                      {mod.summary}
                    </p>

                    {/* Tabular Metadata Box */}
                    <div className="rounded-sm border border-border bg-muted/30 p-3 space-y-2.5 text-xs font-sans">
                      <div className="flex items-center justify-between border-b border-border/60 pb-1.5">
                        <span className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Deliverable
                        </span>
                        <span className="font-medium text-foreground text-right truncate max-w-[140px]">
                          {mod.deliverable}
                        </span>
                      </div>

                      <div className="flex items-center justify-between border-b border-border/60 pb-1.5">
                        <span className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Focus
                        </span>
                        <span className="font-medium text-foreground text-right truncate max-w-[140px]">
                          {mod.focusArea}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Output
                        </span>
                        <span className="font-medium text-foreground text-right truncate max-w-[140px]">
                          {mod.outputFormat}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer Feature Item */}
                  <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-sans">
                    <span className="flex items-center gap-1.5 text-[11px]">
                      <Check className="size-3.5 text-primary stroke-[2]" />
                      Notion pre-built formula
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground uppercase">
                      Ready
                    </span>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Banner with Fast Action */}
        <div className="mt-12 p-6 sm:p-8 rounded-sm border border-border bg-card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-sm bg-primary/10 border border-primary/20 shrink-0 mt-0.5">
              <Layers className="size-5 text-primary stroke-[1.5]" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-serif font-semibold text-foreground">
                All four modules connect inside one unified workspace
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground font-sans">
                Duplicate directly into your private Notion account with one click.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm uppercase tracking-wider text-xs px-6 py-2.5 h-10 font-sans font-semibold">
              <Link to="/AboutDiscoveryConsultationPage">
                <span>Access the Suite</span>
                <ArrowUpRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}